/**
 * What a sandboxed app is allowed to SYNC (RFC 0275, ported from Patchwork's
 * `@patchwork/isolation`).
 *
 * Giving an app a real Automerge handle is what makes collaborative editing
 * possible inside a sandbox: the handle never crosses postMessage, the DOCUMENT
 * syncs over a MessagePort and the app builds its own handle from its own repo.
 * The moment that channel exists, it needs a gate, because a repo connection
 * serves whatever is asked of it. Without one, `mod.open(anyId)` would return
 * any document in the project, which is precisely the isolation the sandbox
 * exists to provide.
 *
 * This is the gate. It holds two sets and answers one question: may this app
 * sync this document.
 *
 * THE RULE THAT MATTERS, and it is the one Patchwork's threat model turns on:
 *
 *   "once a tool discovers an automerge URL, it will be able to access and
 *    modify that document ... These documents can be best protected by
 *    preventing the tool from learning their URLs."
 *
 * So this is a backstop, not the primary defence. An id an app never learns is
 * safe; an id it learns is eventually reachable, because it can write that id
 * into a document it IS allowed and pick it up on a later load. Read every
 * decision here as "reduce what leaks", not "make leaks harmless".
 *
 * DIFFERENCE FROM PATCHWORK WORTH KNOWING. Their ids are `automerge:` URLs, so
 * they can scan a document's content recursively for anything URL-shaped and
 * allowlist it. Ours are bare base58 strings, indistinguishable from ordinary
 * prose: this document's own text contains several. Scanning text for id-shaped
 * runs would hand an app a document because someone pasted an id into a comment.
 * So discovery here walks STRUCTURED FIELDS only, and admits an id only if the
 * project catalog actually knows it.
 */

import { findDocumentRefs } from '@mod/core/model'

/** Ids the app may sync, and why the answer can change over time. */
export interface SyncGateState {
  /** Seeded from what the app was granted, then widened by discovery. */
  allow: Set<string>
  /** Never syncable, whatever the allowlist says. Takes precedence. */
  deny: Set<string>
  /** Until the denylist is populated, everything is refused. */
  ready: boolean
}

/**
 * A document id, as this project writes them.
 *
 * Base58-ish, 20+ chars, no separators. Deliberately NOT applied to free text
 * (see the header): this only screens candidates already found in a structured
 * field, so that a malformed value cannot become a lookup.
 */
const ID_SHAPE = /^[1-9A-HJ-NP-Za-km-z]{20,}$/

export function looksLikeDocumentId(value: unknown): value is string {
  return typeof value === 'string' && ID_SHAPE.test(value)
}

export function createSyncGate(): SyncGateState {
  return { allow: new Set(), deny: new Set(), ready: false }
}

/**
 * May this app sync this document?
 *
 * FAILS CLOSED in three ways, each deliberate: before the denylist is ready,
 * for anything denied, and for anything not explicitly allowed. An id the app
 * invented gets nothing. Patchwork prompts the user at this point; we do not
 * yet, and until we decide what that prompt is (a modal from a sandboxed app
 * mid-session is the wrong surface, and an agent-driven app would trip it
 * constantly) refusing is the honest behaviour rather than a silent grant.
 */
export function allowsSync(gate: SyncGateState, documentId: string): boolean {
  if (!gate.ready) return false
  if (gate.deny.has(documentId)) return false
  return gate.allow.has(documentId)
}

/**
 * The documents an app is NEVER given, populated before anything is allowed.
 *
 * The project document is the one that matters most and is easy to miss: it
 * is the catalog, so handing it over hands over the id of every document and
 * session in the project at once. Under the rule above that is not one leak,
 * it is all of them, and no per-document allowlist afterwards would matter.
 */
export function denyInfrastructure(
  gate: SyncGateState,
  ids: {
    projectId: string
    /** Every session in the project: an app is never given a transcript. */
    sessionIds?: string[]
    /** The World (RFC 0261): devices, placement, other projects. */
    worldId?: string | null
    /** Other apps' view and source documents: code, but also other apps' data
     *  paths. Patchwork denylists tool source for the same reason. */
    appDocIds?: string[]
  },
): SyncGateState {
  gate.deny.add(ids.projectId)
  for (const id of ids.sessionIds ?? []) gate.deny.add(String(id))
  if (ids.worldId) gate.deny.add(ids.worldId)
  for (const id of ids.appDocIds ?? []) gate.deny.add(id)
  gate.ready = true
  return gate
}

/**
 * Seed the allowlist from what the app was actually granted.
 *
 * Nothing new is invented here: an app's instance (RFC 0271), whatever it
 * declared as its subject and includes (RFC 0273 `surface.setSubject`), and its
 * capability grants (RFC 0270) are already computed elsewhere. This turns three
 * existing notions of "what this app is for" into one list.
 *
 * Deny wins, checked per id rather than at the end, so a denied id cannot enter
 * the set at all and a later bug that reads `allow` directly still cannot find
 * it there.
 */
export function seedAllow(
  gate: SyncGateState,
  rootIds: string[],
): SyncGateState {
  for (const id of rootIds) {
    if (!id || gate.deny.has(id)) continue
    gate.allow.add(id)
  }
  return gate
}

/**
 * Widen the allowlist to the documents a granted document REFERS to.
 *
 * Patchwork's reasoning, which holds here: if the user opened a document, the
 * documents it links are authorised for the app rendering it. A review that
 * names a plan is useless if the app may read the review and not the plan.
 *
 * Structured values only, and only ids the catalog knows. `known` is the
 * membership test that keeps a base58-looking string in someone's prose from
 * becoming a grant: an id that names nothing in this project is not a
 * reference, it is a coincidence.
 */
export function discoverFrom(
  gate: SyncGateState,
  value: unknown,
  known: (id: string) => boolean,
  depth = 0,
): SyncGateState {
  // REFERENCES FIRST, and they need no schema and no membership test. An
  // `automerge:<id>` in a string is a reference by construction, including in
  // prose, which is the property the bare form could never have. This is
  // Patchwork's transitive rule verbatim; everything below it is the fallback
  // for values written before references had a form.
  if (typeof value === 'string') {
    for (const id of findDocumentRefs(value)) {
      if (!gate.deny.has(id)) gate.allow.add(id)
    }
  }
  // Bounded because a document is a CRDT that can contain a cycle through
  // history or a deeply nested map, and an unbounded walk here would hang the
  // host rather than the app.
  if (depth > 6 || value == null) return gate
  // TRANSITIONAL: a bare id in a structured field, written before references
  // had a form. `known` is what stops a base58-looking string in someone's
  // prose becoming a grant -- an id that names nothing here is a coincidence,
  // not a reference. Delete this branch once stored references are all urls;
  // the block above already covers them and needs no heuristic.
  if (looksLikeDocumentId(value)) {
    if (!gate.deny.has(value) && known(value)) gate.allow.add(value)
    return gate
  }
  if (Array.isArray(value)) {
    for (const v of value) discoverFrom(gate, v, known, depth + 1)
    return gate
  }
  if (typeof value === 'object') {
    for (const v of Object.values(value as Record<string, unknown>)) {
      discoverFrom(gate, v, known, depth + 1)
    }
  }
  return gate
}
